import delve from 'dlv'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useScript from 'hooks/useScript'
import fetch from 'isomorphic-unfetch'
import { useRef, useState } from 'react'
import { amplitudeTrack } from 'src/lib/Amplitude/analytics'

const useHubSpotForm = ({ portalId, formId, target }) => {
  const [loaded, error] = useScript({ src: '//js.hsforms.net/forms/v2.js' })
  const [formCreated, setFormCreated] = useState(false)
  const formRef = useRef(null)
  const dataRef = useRef(null)

  const onFormReady = async (event) => {
    if (
      event.data.type === 'hsFormCallback' &&
      event.data.eventName === 'onFormReady'
    ) {
      const frame = document.getElementById('hs-form-iframe-0')
      const form = frame?.contentWindow.document.querySelector('.hbspt-form')

      formRef.current = form
    }
  }

  const onFormSubmit = async (event) => {
    if (
      event.data.type === 'hsFormCallback' &&
      event.data.eventName === 'onFormSubmit' &&
      Array.isArray(event.data.data)
    ) {
      const response = await fetch('/api/demo', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstname: event.data.data.find((item) => item.name === 'firstname')
            ?.value,
          lastname: event.data.data.find((item) => item.name === 'lastname')
            ?.value,
          email: event.data.data.find((item) => item.name === 'email')?.value,
          duration: event.data.data.find(
            (item) => item.name === 'demo_duration'
          )?.value,
        }),
      })
      dataRef.current = await response.json()
    }
  }

  const onFormSubmitted = async (event) => {
    if (
      event.data.type === 'hsFormCallback' &&
      event.data.eventName === 'onFormSubmitted'
    ) {
      let i = 0
      let tries = 4
      formRef.current.innerHTML = `<div class="submitted-message hs-main-font-element" data-reactid=".hbspt-forms-0"><p style="color: #33475b;">Our operator is checking if there is any available demo, please wait... (20s)</p></div>`
      while (dataRef.current === null) {
        await new Promise((resolve) => setTimeout(resolve, 5000))
        i += 1

        if (i == tries) {
          break
        }
      }
      if (i != tries) {
        formRef.current.innerHTML = `<div class="submitted-message hs-main-font-element" style="color: #33475b;" data-reactid=".hbspt-forms-0">
        <p style="color: #33475b;">Your demo is waiting for you! 🎉 <br> It may take a moment to boot up—hang tight! (5 min max)</p>
        <p style="color: #33475b;">Backend URL 👉 <a href="${delve(
          dataRef.current,
          'backend'
        )}/admin" target="_blank"> Strapi admin</a><br>Frontend URL 👉 <a href="${delve(
          dataRef.current,
          'frontend'
        )}" target="_blank"> Next.js app</a>
        </p>
        <p style="color: #33475b;">🔐 Credentials for Strapi admin
        <br>
        Email: <b>admin@strapidemo.com</b>
        <br>
        Password: <b>welcomeToStrapi123</b>
        </p>
      <p style="color: #33475b;">You'll receive an email with more information 📧 <br> If you have any issues, please contact us at demo@strapi.io</p></div>`
      } else {
        formRef.current.innerHTML = `<div class="submitted-message hs-main-font-element" data-reactid=".hbspt-forms-0"><p style="color: #33475b;">We successfully received your request! Our operator will take care to deploy your demo in the cloud ☁️ </p><p style="color: #33475b;">You'll receive an email with more information 📧 <br> If you have any issues, please contact us at demo@strapi.io</p></div>`
      }
    }
    amplitudeTrack('didSubmitHubspotForm', { id: event?.data?.id })
  }

  useIsomorphicLayoutEffect(() => {
    // If this is the demo form
    if (formId === '9a13a962-c454-4eab-a8f1-e1d0348396d3') {
      window.addEventListener('message', onFormReady)

      return () => {
        window.removeEventListener('message', onFormReady)
      }
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    // If this is the demo form
    if (formId === '9a13a962-c454-4eab-a8f1-e1d0348396d3') {
      window.addEventListener('message', onFormSubmit)

      return () => {
        window.removeEventListener('message', onFormSubmit)
      }
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    // If this is the demo form
    if (formId === '9a13a962-c454-4eab-a8f1-e1d0348396d3') {
      window.addEventListener('message', onFormSubmitted)

      return () => {
        window.removeEventListener('message', onFormSubmitted)
      }
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    if (loaded && window.hbspt && !formCreated) {
      const fetchCountriesToShow = async () => {
        const response = await fetch('/api/country')
        return response.json()
      }

      fetchCountriesToShow().then((data) => {
        window.hbspt.forms.create({
          portalId,
          formId,
          target,
          onFormReady: function ($form) {
            // Now handle the conditional logic
            if (!!data?.data?.country) {
              return
            } else {
              // Remove existing .hs-richtext elements
              $form.querySelectorAll('.hs-richtext').forEach((element) => {
                element.remove()
              })

              // Create and append the new consentRichtext element
              const consentRichtextDiv = document.createElement('div')
              const consentRichtextP = document.createElement('p')
              consentRichtextP.innerText =
                'Strapi needs the contact information you provide to us to contact you about our products and services. You may unsubscribe from these communications at anytime. For information on how to unsubscribe, as well as our privacy practices and commitment to protecting your privacy, check out our Privacy Policy'
              consentRichtextDiv.classList.add('hs-richtext')
              const legalConsentDiv = $form.getElementsByClassName(
                'legal-consent-container'
              )
              consentRichtextDiv.appendChild(consentRichtextP)
              legalConsentDiv[0].appendChild(consentRichtextDiv)
            }
            $form.querySelectorAll('.hs-richtext').forEach((element) => {
              element.querySelectorAll('p').forEach((pElement) => {
                pElement.style.fontSize = '10px' // Change '16px' to your desired font size
                pElement.style.lineHeight = '1.8'
              })
            })
          },
        })
        setFormCreated(true)
      })
    }
  }, [loaded, formCreated, setFormCreated])

  return { loaded, formCreated, error }
}

export default useHubSpotForm
